/**
 * Service for Advertisements API
 * Manages advertisements for profiles and places
 */

import { getUser } from './authService';

const API_BASE_URL = 'https://apicabaza.creago.be/rest/cabaza/advertisements';

/**
 * Map of lowercase keys to camelCase
 */
const keyMapping = {
    peopleid: 'peopleId',
    placeuuid: 'placeUuid',
    createdat: 'createdAt',
    updatedat: 'updatedAt',
    firstname: 'firstName',
    lastname: 'lastName',
    peopleuuid: 'peopleUuid',
    imagepath: 'imagePath',
    linkurl: 'linkUrl',
    displaytype: 'displayType',
    displayorder: 'displayOrder',
    isactive: 'isActive',
    startdate: 'startDate',
    enddate: 'endDate',
    viewcount: 'viewCount',
    clickcount: 'clickCount',
    placename: 'placeName'
};

/**
 * Normalize object keys to camelCase
 */
const normalizeKeys = (obj) => {
    if (obj === null || obj === undefined) return obj;
    if (Array.isArray(obj)) {
        return obj.map(item => normalizeKeys(item));
    }
    if (typeof obj === 'object') {
        return Object.fromEntries(
            Object.entries(obj).map(([key, value]) => {
                const lowerKey = key.toLowerCase();
                const newKey = keyMapping[lowerKey] || lowerKey;
                return [newKey, normalizeKeys(value)];
            })
        );
    }
    return obj;
};

/**
 * Get user's advertisements
 * @param {Object} options - Query options
 * @returns {Promise<Array>} Advertisements array
 */
export const getMyAdvertisements = async (options = {}) => {
    try {
        const user = getUser();
        if (!user?.id) {
            console.error('No user logged in');
            return [];
        }

        const params = new URLSearchParams({
            peopleId: user.id.toString()
        });

        if (options.displayType) {
            params.append('displayType', options.displayType);
        }
        if (options.isActive !== undefined) {
            params.append('isActive', options.isActive.toString());
        }

        const response = await fetch(`${API_BASE_URL}?${params}`, {
            method: 'GET',
            credentials: 'include'
        });

        const result = await response.json();
        return normalizeKeys(result.data ?? result.DATA ?? []);
    } catch (error) {
        console.error('Error fetching advertisements:', error);
        return [];
    }
};

/**
 * Get advertisements for a profile (public)
 * @param {string} peopleUuid - People UUID
 * @returns {Promise<Array>} Advertisements array
 */
export const getProfileAdvertisements = async (peopleUuid) => {
    try {
        const response = await fetch(`${API_BASE_URL}/profile/${peopleUuid}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        const result = await response.json();
        return normalizeKeys(result.data ?? result.DATA ?? []);
    } catch (error) {
        console.error('Error fetching profile advertisements:', error);
        return [];
    }
};

/**
 * Get advertisement by UUID (public)
 * @param {string} adUuid - Advertisement UUID
 * @returns {Promise<Object|null>} Advertisement object or null
 */
export const getAdvertisementByUuid = async (adUuid) => {
    try {
        const response = await fetch(`${API_BASE_URL}/uuid/${adUuid}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            return null;
        }

        const result = await response.json();
        return normalizeKeys(result.data ?? result.DATA ?? null);
    } catch (error) {
        console.error('Error fetching advertisement:', error);
        return null;
    }
};

/**
 * Get advertisements for a place (public)
 * @param {string} placeUuid - Place UUID
 * @returns {Promise<Array>} Advertisements array
 */
export const getPlaceAdvertisements = async (placeUuid) => {
    try {
        const response = await fetch(`${API_BASE_URL}/place/${placeUuid}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        const result = await response.json();
        return normalizeKeys(result.data ?? result.DATA ?? []);
    } catch (error) {
        console.error('Error fetching place advertisements:', error);
        return [];
    }
};

/**
 * Create a new advertisement
 * @param {Object} adData - Advertisement data
 * @returns {Promise<Object>} Result with created advertisement
 */
export const createAdvertisement = async (adData) => {
    try {
        const user = getUser();
        if (!user?.id) {
            return {
                success: false,
                message: 'Vous devez être connecté pour créer une publicité'
            };
        }

        const response = await fetch(API_BASE_URL, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                ...adData,
                peopleId: user.id
            })
        });

        const text = await response.text();
        if (!text || text.trim() === '') {
            return {
                success: false,
                message: 'Service non disponible. Veuillez réessayer plus tard.'
            };
        }

        const result = JSON.parse(text);

        if (!response.ok) {
            throw new Error(result.message || result.MESSAGE || 'Error creating advertisement');
        }

        return {
            success: true,
            data: normalizeKeys(result.data ?? result.DATA),
            message: result.message ?? result.MESSAGE
        };
    } catch (error) {
        console.error('Error creating advertisement:', error);
        return {
            success: false,
            message: error.message
        };
    }
};

/**
 * Update an advertisement
 * @param {number} adId - Advertisement ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Result with updated advertisement
 */
export const updateAdvertisement = async (adId, updates) => {
    try {
        const user = getUser();
        if (!user?.id) {
            return {
                success: false,
                message: 'Vous devez être connecté pour modifier une publicité'
            };
        }

        const response = await fetch(`${API_BASE_URL}/${adId}`, {
            method: 'PUT',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                ...updates,
                peopleId: user.id
            })
        });

        const result = await response.json();

        if (!response.ok) {
            throw new Error(result.message || result.MESSAGE || 'Error updating advertisement');
        }

        return {
            success: true,
            data: normalizeKeys(result.data ?? result.DATA),
            message: result.message ?? result.MESSAGE
        };
    } catch (error) {
        console.error('Error updating advertisement:', error);
        return {
            success: false,
            message: error.message
        };
    }
};

/**
 * Delete an advertisement
 * @param {number} adId - Advertisement ID
 * @returns {Promise<Object>} Result
 */
export const deleteAdvertisement = async (adId) => {
    try {
        const user = getUser();
        if (!user?.id) {
            return {
                success: false,
                message: 'Vous devez être connecté pour supprimer une publicité'
            };
        }

        const params = new URLSearchParams({
            peopleId: user.id.toString()
        });

        const response = await fetch(`${API_BASE_URL}/${adId}?${params}`, {
            method: 'DELETE',
            credentials: 'include'
        });

        const result = await response.json();

        return {
            success: response.ok,
            message: result.message ?? result.MESSAGE,
            imagePath: result.imagePath ?? result.IMAGEPATH
        };
    } catch (error) {
        console.error('Error deleting advertisement:', error);
        return {
            success: false,
            message: error.message
        };
    }
};

/**
 * Track an advertisement click
 * @param {number} adId - Advertisement ID
 */
export const trackClick = async (adId) => {
    try {
        await fetch(`${API_BASE_URL}/${adId}/click`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        });
    } catch (error) {
        console.error('Error tracking click:', error);
    }
};

/**
 * Track an advertisement view
 * @param {number} adId - Advertisement ID
 */
export const trackView = async (adId) => {
    try {
        await fetch(`${API_BASE_URL}/${adId}/view`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        });
    } catch (error) {
        console.error('Error tracking view:', error);
    }
};

export default {
    getMyAdvertisements,
    getAdvertisementByUuid,
    getProfileAdvertisements,
    getPlaceAdvertisements,
    createAdvertisement,
    updateAdvertisement,
    deleteAdvertisement,
    trackClick,
    trackView
};
